import React, { useCallback, useLayoutEffect, useMemo } from 'react';
import { useNavigate, useAsyncValue, useRevalidator } from 'react-router-dom';
import { useRevalidateOnSync } from '../../../../../../offline/useRevalidateOnSync';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

// Components Styled
import {
  AttentionAccidentTableStyled,
  Container,
  ContainerContent,
  MainButtonStyled,
  SubContainerContent,
  SummaryContainer,
  TableContainer,
} from './styles';

//Components
import AccidentContentCardResume from '../../../../../../components/display/AccidentContentCardResume';
import { WorkOrder } from '../../../../../../components/display/ShowOT/WorkOrder';
import { MainButton } from '../../../../../../components/inputs/buttons/MainButton';

// Types
import { AccidentResume } from '../../../../../../types/accident';
import { VehiclesInvolvedSummary } from '../../../../../../components/display/tables/summaryTables/VehiclesInvolvedSummary';
import { useForm } from 'react-hook-form';
import { Typography } from '../../../../../../components/display/Typography';
import { Order, OrderState, OrderType } from '../../../../../../types/orders';
import { Role } from '../../../../../../types/auth';
import { useAuth } from '../../../../../../hooks/useAuth';
import { variant } from '../../../../../../components/display/SpecialTag/types';
import { VehiclesInvolvedType } from '../../../../../../components/display/tables/summaryTables/VehiclesInvolvedSummary/types';
import { subCapitolsType, predefinedOrderType } from '../../../../../../components/display/ShowOT/WorkOrder/types';

// Utils
import { ImageData } from '../../../../../../components/display/ResumeLoadImages/types';
import { decompressSessionStorage } from '../../../../../../utils/sessionStorage';
import { isInternalPath } from '../../../../../../utils/internalPath';
import { formAccidentNaming } from '../../../../../../components/forms/AddBasicInfoAccidentForm/constants';

// Redux
import { roidOTSelected } from '../../../../../../state/actions/ui/roidOTSelectedGeneralView';
import { InputTextArea } from '../../../../../../components/inputs/InputTextArea';
import { tabs } from '../../../../../../state/actions/ui/tabs';
import { tabsType } from '../../../../../../state/reducers/ui/tabs';
import { groupLanes, groupTracks } from '../../../../../../data/orders';

export default function AccidentSummaryGeneralView(): JSX.Element {
  const loaderData = useAsyncValue() as AccidentResume &
    Order & {
      orderId: string;
      roid: string;
      vehicles: VehiclesInvolvedType[];
      damages: string;
      stateOrderExpeditionParent?: string;
      margin: string;
      direction: string;
      lane: string;
      track: string;
      predefine?: string;
      predefinedOrders?: predefinedOrderType[];
    };
  const { t } = useTranslation(['accidents', 'pages', 'buttons']);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const revalidator = useRevalidator();
  useRevalidateOnSync();
  const { user } = useAuth();
  const currentRole = user?.user?.rolActive as Role | undefined;

  const { control } = useForm();
  const accidentData = loaderData;

  // Read navigation context from session storage
  const navigationContext = useMemo(() => {
    const raw = sessionStorage.getItem(
      formAccidentNaming.ACCIDENT_NAVIGATION_CONTEXT
    );
    if (!raw) return null;
    try {
      return JSON.parse(decompressSessionStorage(raw));
    } catch {
      return null;
    }
  }, []);

  const navigationOrigin = navigationContext?.origin || 'accidents-table';
  const parentOrderId = navigationContext?.parentOrderId;
  const parentRoid = navigationContext?.parentRoid;
  const returnTo = navigationContext?.returnTo as string | undefined;

  // Detect if parent order is a campaign
  const isCampaign = navigationContext?.parentOrderType === OrderType.Campaign
    || parentRoid?.startsWith('Ca');

  // Loader fallido: el selector lanzó (offline sin cache, 404, 403, etc.) o
  // devolvió un payload sin identificador reconocible. Protegemos render Y
  // effects que dependen de `accidentData.roid`/`.orderId` para evitar crash.
  //
  // IMPORTANTE: `selectAccidentById` devuelve `{ orderId, roid, accidentID, ... }`
  // pero NO un campo `id` literal. Usamos `roid` como flag de "datos cargados"
  // — está siempre presente cuando el selector tiene éxito, y nunca en el
  // payload vacío `{ predefinedOrders, operator }` que devuelve el catch.
  // Detalle: docs/superpowers/plans/2026-05-12-offline-edit-action-accident.md § Fase 3.
  const loaderFailed = !accidentData || !accidentData.roid;

  useLayoutEffect(() => {
    if (loaderFailed) return;
    dispatch(
      roidOTSelected(
        OrderType.Accident,
        Array.isArray(accidentData.roid)
          ? accidentData.roid.join('.')
          : accidentData.roid,
        accidentData.orderId
      )
    );
  }, [accidentData, loaderFailed]);

  const handleContinueAccident = useCallback(() => {
    // Clean up navigation context
    sessionStorage.removeItem(formAccidentNaming.ACCIDENT_NAVIGATION_CONTEXT);

    // Context-aware redirect
    if (navigationOrigin === 'workorder' && parentOrderId) {
      // Use campaign route if parent is a campaign
      const targetRoute = isCampaign
        ? `/work-orders/campaign/${parentOrderId}`
        : `/work-orders/${parentOrderId}`;
      navigate(targetRoute);
    } else if (isInternalPath(returnTo)) {
      // Vuelta al listado del que se salió. Las pestañas del listado de
      // campañas (`GeneralViewCampaign`) ya escribían su `returnTo` en el
      // contexto desde 2026-07-12, pero este handler no lo miraba: editabas
      // un accidente DESDE ESA PESTAÑA y el Continuar te soltaba en la tabla
      // global de Accidentes. Reportado por el usuario 2026-08-05 con el
      // flujo de comunicaciones como referencia.
      navigate(returnTo);
    } else {
      dispatch(tabs(tabsType.WORK_ORDERS_GENERAL, 1));
      navigate('/work-orders/accidents');
    }
  }, [navigationOrigin, parentOrderId, isCampaign, returnTo, navigate, dispatch]);

  const handleRevalidateData = useCallback(() => {
    revalidator.revalidate();
  }, [revalidator]);

  if (loaderFailed) {
    const isOffline = typeof navigator !== 'undefined' && !navigator.onLine;
    const titleKey = isOffline
      ? 'accidents:errors.accidentOfflineNotCached'
      : 'accidents:errors.accidentNotFound';
    const descKey = isOffline
      ? 'accidents:errors.accidentOfflineNotCachedDescription'
      : 'accidents:errors.accidentNotFoundDescription';
    return (
      <Container>
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <Typography size="L" variant="semiBold" style={{ color: '#d32f2f' }}>
            {t(titleKey)}
          </Typography>
          <Typography size="M" style={{ marginTop: '0.5rem', color: '#666' }}>
            {t(descKey)}
          </Typography>
          <MainButton
            text={t('buttons:back', 'Volver')}
            onClick={() => navigate(-1)}
            style={{ marginTop: '1rem' }}
          />
        </div>
      </Container>
    );
  }

  return (
    <Container>
      {accidentData && (
        <Container>
          <WorkOrder
            id={accidentData.orderId as string}
            name={t('pages:workOrders.name')}
            roid={accidentData.roid as string}
            type={accidentData.type as OrderType}
            tag={accidentData.state as variant}
            capitol={accidentData.capitol}
            subCapitol={accidentData.subCapitol}
            concession={accidentData.concession}
            road={accidentData.road}
            pkInit={(accidentData.pkInit as string) || ''}
            pkEnd={(accidentData.pkEnd as string) || ''}
            description={accidentData.description as string}
            notes={accidentData.notes}
            date={accidentData.registerInitDate as string}
            modelTypeId={accidentData.id as string}
            capitols={accidentData.capitols}
            subCapitolsValues={
              accidentData.subCapitolsValues as subCapitolsType[]
            }
            concessions={accidentData.concessions}
            roadsValues={accidentData.roadsValues}
            disabled={
              loaderData.state === OrderState.End ||
              loaderData.state === OrderState.Annulled ||
              loaderData.stateOrderExpeditionParent === OrderState.End ||
              loaderData.stateOrderExpeditionParent === OrderState.Annulled ||
              currentRole === Role.RoomTeam ||
              (currentRole === Role.Tablet && loaderData.stateOrderExpeditionParent !== OrderState.WorkInProgress)
            }
            margin={accidentData.margin}
            lane={accidentData.lane}
            track={accidentData.track}
            direction={accidentData.direction}
            groupLanes={groupLanes}
            groupTracks={groupTracks}
            revalidatorFunction={handleRevalidateData}
            // ROID = `<TypeCode>.<indexExpedition>.<indexType>.<class>` (autoridad
            // backend generateROID). El segmento [1] es indexExpedition; [2] es
            // indexType. Antes pasábamos [2] → el ChangeStateModal hacía
            // FETCH_ORDERS con la expedición equivocada (o vacía), no encontraba
            // orders y, como solo renderiza si orders.length > 0, salía vacío:
            // "no se podía cambiar el estado del accidente desde dentro".
            indexExpedition={Number(accidentData.roid?.split('.')[1])}
            state={accidentData.state}
            predefine={accidentData.predefine}
            predefinedOrders={accidentData.predefinedOrders}
          />

          <ContainerContent>
            <AccidentContentCardResume
              creatorName={accidentData.creatorName}
              registeredByName={accidentData.registeredByName}
              mediumKnowledge={accidentData.mediumKnowledge}
              laneCutting={accidentData.laneCutting}
              policeReport={accidentData.policeReport}
              surveillanceBody={accidentData.surveillanceBody}
              policeReportNumber={accidentData?.policeReportNumber}
              weather={accidentData.weather}
              surfaceCondition={accidentData.surfaceCondition}
              luminosity={accidentData.luminosity}
              conditionsDriving={accidentData.conditionsDriving}
              roadConditionDescription={accidentData.roadConditionDescription}
              images={accidentData.images as ImageData[]}
            />
            <SubContainerContent>
              <SummaryContainer>
                <TableContainer>
                  <Typography component="h3" variant="semiBold">
                    {t('accidents:accidentAttention')}
                  </Typography>
                  <AttentionAccidentTableStyled
                    disabled
                    control={control}
                    defaultValues={accidentData}
                  />
                </TableContainer>
              </SummaryContainer>

              {(accidentData?.vehicles?.length > 0 || accidentData.damages) && (
                <SummaryContainer>
                  {accidentData?.vehicles.length > 0 && (
                    <TableContainer>
                      <Typography component="h3" variant="semiBold">
                        {t('accidents:vehiclesInvolved')}
                      </Typography>
                      <VehiclesInvolvedSummary data={accidentData.vehicles} />
                    </TableContainer>
                  )}
                  {accidentData.damages && (
                    <TableContainer>
                      <Typography component="h3" variant="semiBold">
                        {t('accidents:damages')}
                      </Typography>
                      <InputTextArea
                        label=""
                        disabled
                        value={accidentData.damages}
                      />
                    </TableContainer>
                  )}
                </SummaryContainer>
              )}
              <MainButtonStyled
                type="button"
                text={t('accidents:continue')}
                onClick={handleContinueAccident}
              />
            </SubContainerContent>
          </ContainerContent>
        </Container>
      )}
    </Container>
  );
}
